import { mdiArrowDown, mdiBell } from '@mdi/js';
import Icon from '@mdi/react';
import { Button } from '@mollybet/ui';
import { Currency } from 'common/classes/currency';
import { StyledButton } from 'common/components/buttons';
import { ExpandingContainer } from 'common/components/expandingContainer';
import { BaseTable, ColumnProp } from 'common/components/table/base';
import { TableTitle } from 'common/components/table/title';
import { useProducerWithThunks } from 'common/hooks/useProducer';
import {
  CustomerOutstanding,
  CustomerSummary,
} from 'common/state/customers/actions';
import { customerOutstandingsRecipe } from 'common/state/customers/recipes';
import { defaultCustomerOutstandingsState } from 'common/state/customers/states';
import { doGetCustomerOutstandings } from 'common/state/customers/thunkActions';
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { Link } from 'react-router-dom';
import styled from 'styled-components';
import { ModalMode } from '../rencocile';
import { ReconcileRow } from './modalCards';

export interface CustomerOutstandingsTableProps {
  setSelectedReconcileRow: (customer: ReconcileRow) => void;
  setSelectedCurrencyConvertRow: (customer: CustomerOutstanding) => void;
  setModalMode: (mode: ModalMode) => void;
  shouldRefresh: boolean;
  customerSummaries: CustomerSummary[];
  isLoading?: boolean;
  error?: React.ReactNode;
}

export const UsernameLink = styled(Link)`
  width: fit-content;
  padding-bottom: 0.125rem;
  border-bottom: 1px dashed;
  cursor: pointer;
  color: inherit;
  text-decoration: none;
`;

const GroupByButtons = styled.div`
  display: flex;
  align-items: center;

  > * {
    margin-right: 0.5rem;
  }
`;

const GroupByTitle = styled.span`
  font-size: 0.75rem;
  text-transform: uppercase;
  font-weight: bold;
`;

export const MovementsLink = styled(Link)`
  color: inherit;
  text-decoration: none;
  cursor: pointer;
  display: block;
  padding: 0.25rem;
  text-align: center;

  &:hover {
    background: ${({ theme }) => theme.cardLevel1};
  }
`;

export const MovementsRow = styled.div`
  color: inherit;
  text-decoration: none;
  cursor: pointer;
  display: block;
  padding: 0.25rem;
  text-align: center;

  &:hover {
    background: ${({ theme }) => theme.cardLevel1};
  }
`;

export const AmountContainer = styled.div`
  height: 100%;
  display: flex;
  align-items: center;
  cursor: pointer;

  .currency {
    flex: 1;
    margin-right: 0.25rem;

    .value {
      text-decoration: underline;
    }
  }
`;

export const SubRowContainer = styled.div`
  grid-column: 5;
  font-size: 0.875rem;
`;

const SubRowGenerator =
  (
    expandedRows: Set<number>,
    fullyExpandedRows: Set<number>,
    setFullyExpandedRows: (rows: Set<number>) => void
  ) =>
  ({ row, index }) => {
    return (
      <SubRowContainer>
        <ExpandingContainer visible={expandedRows.has(index)}>
          {fullyExpandedRows.has(index) ? (
            row.account_codes.map((code) => (
              <MovementsLink key={code} to={`/movements/${code}`}>
                {code.replace(/contact.[0-9]+.customer.[0-9]+./, '')}
              </MovementsLink>
            ))
          ) : (
            <>
              <MovementsLink
                key={row.account_codes[0]}
                to={`/movements/${row.account_codes[0]}`}
              >
                {row.account_codes[0].replace(
                  /contact.[0-9]+.customer.[0-9]+./,
                  ''
                )}
              </MovementsLink>
              {row.account_codes.length > 1 && (
                <MovementsRow
                  onClick={() => {
                    setFullyExpandedRows(
                      new Set([...fullyExpandedRows, index])
                    );
                  }}
                >
                  + {row.account_codes.length - 1} more accounts...
                </MovementsRow>
              )}
            </>
          )}
        </ExpandingContainer>
      </SubRowContainer>
    );
  };

export const CustomerOutstandingsTable = ({
  setSelectedReconcileRow,
  setSelectedCurrencyConvertRow,
  setModalMode,
  shouldRefresh,
  customerSummaries,
  isLoading,
  error,
}: CustomerOutstandingsTableProps) => {
  const [groupSystem, setGroupSystem] = useState(false);
  const [groupAccount, setGroupAccount] = useState(true);
  const [groupStream, setGroupStream] = useState(false);
  const [outstandingState, outstandingDispatch] = useProducerWithThunks(
    customerOutstandingsRecipe,
    defaultCustomerOutstandingsState
  );
  const [expandedRows, setExpandedRows] = useState<Set<number>>(new Set());
  const [fullyExpandedRows, setFullyExpandedRows] = useState<Set<number>>(
    new Set()
  );
  const fetchTimeout = useRef<ReturnType<typeof setTimeout>>();

  const fetchOustandings = useCallback(() => {
    outstandingDispatch(
      doGetCustomerOutstandings({
        group_system: groupSystem ? 1 : 0,
        group_account: groupAccount ? 1 : 0,
        group_stream: groupStream ? 1 : 0,
      })
    );
    setExpandedRows(new Set());
  }, [outstandingDispatch, groupSystem, groupAccount, groupStream]);

  useEffect(() => {
    if (fetchTimeout.current == null) {
      fetchOustandings();
    } else {
      clearTimeout(fetchTimeout.current);
      setTimeout(fetchOustandings, 500);
    }
  }, [fetchOustandings]);

  useEffect(() => {
    if (shouldRefresh) fetchOustandings();
  }, [fetchOustandings, shouldRefresh]);

  const reconcileCustomer = useCallback(
    (customerOutstanding: CustomerOutstanding) => {
      setSelectedReconcileRow({
        username: customerOutstanding.customer_username,
        currency: customerOutstanding.currency,
        amount: customerOutstanding.amount,
        account: customerOutstanding.account_username,
        threshold: customerSummaries?.find(
          (summary) => summary.customer_id === customerOutstanding.customer_id
        )?.wallet_threshold,
        system: customerOutstanding.system,
        stream: customerOutstanding.stream,
      });
      setModalMode('customerReconcile');
    },
    [setSelectedReconcileRow, setModalMode, customerSummaries]
  );

  const columns = useMemo(
    (): ColumnProp<CustomerOutstanding>[] => [
      {
        key: 'customer_username',
        title: 'Username',
        valueFormatter: (row) => (
          <UsernameLink to={`/customers/${row.customer_username}`}>
            {row.customer_username}
          </UsernameLink>
        ),
      },
      { key: 'system_name', title: 'System' },
      { key: 'account_username', title: 'Account' },
      { key: 'stream', title: 'Stream' },
      {
        key: 'amount',
        title: 'Amount',
        valueFormatter: (row, index) => (
          <AmountContainer
            onClick={() => {
              const newRows = new Set([...Array.from(expandedRows)]);
              if (newRows.has(index)) newRows.delete(index);
              else newRows.add(index);
              setExpandedRows(newRows);
            }}
          >
            {new Currency(row.amount, row.currency).toElement()}
            <Icon path={mdiArrowDown} size={0.675} />
          </AmountContainer>
        ),
        sortValueGenerator: (row) => parseFloat(row.amount),
        csvFormatter: (row) =>
          new Currency(row.amount, row.currency).toString({ symbol: false }),
      },
      {
        key: 'actions',
        title: 'Actions',
        valueFormatter: (row) => (
          <>
            <StyledButton onClick={() => reconcileCustomer(row)}>
              Reconcile
            </StyledButton>
            <StyledButton
              onClick={() => {
                setSelectedCurrencyConvertRow(row);
                setModalMode('currencyConvert');
              }}
            >
              Convert Currency
            </StyledButton>
          </>
        ),
        isSortable: false,
      },
    ],
    [
      setSelectedCurrencyConvertRow,
      setModalMode,
      expandedRows,
      reconcileCustomer,
    ]
  );

  return (
    <>
      <TableTitle icon={mdiBell} title="Outstanding" />
      <br />
      <GroupByButtons>
        <GroupByTitle>Group By:</GroupByTitle>
        <Button
          variant={groupSystem ? 'primary' : 'secondary'}
          onClick={() => setGroupSystem(!groupSystem)}
        >
          System
        </Button>
        <Button
          variant={groupAccount ? 'primary' : 'secondary'}
          onClick={() => setGroupAccount(!groupAccount)}
        >
          Account
        </Button>
        <Button
          variant={groupStream ? 'primary' : 'secondary'}
          onClick={() => setGroupStream(!groupStream)}
        >
          Stream
        </Button>
      </GroupByButtons>
      <br />
      <BaseTable
        columns={columns}
        rows={outstandingState.outstandings || []}
        csvName="Outstanding"
        isLoading={outstandingState.pending || isLoading}
        SubRow={SubRowGenerator(
          expandedRows,
          fullyExpandedRows,
          setFullyExpandedRows
        )}
        error={outstandingState.error || error}
      />
    </>
  );
};
